import { Reveal, MaskLine } from "@/components/site/Reveal";
import ContactForm from "@/components/site/ContactForm";
import { SITE, PEOPLE, GROUP_FIRMS } from "@/data/content";
import { MapPin, Mail, Globe, Phone, MessageCircle, ExternalLink, ArrowUpRight } from "lucide-react";

const ContactPage = () => (
  <main data-testid="page-contact">
    {/* HERO */}
    <section className="bg-brand-choc pt-36 pb-24 md:pt-48 md:pb-28 text-brand-cream">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <MaskLine><p className="eyebrow-gold">Contact us</p></MaskLine>
        <h1 className="mt-6 font-display font-light tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-8xl">
          <MaskLine delay={0.1}>Talk to the</MaskLine>
          <MaskLine delay={0.3}><span className="italic text-brand-gold">export desk.</span></MaskLine>
        </h1>
        <MaskLine delay={0.5}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-brand-cream/80">
            Bulk orders, private grammages, export documentation — a real person from the family replies, usually the same day.
          </p>
        </MaskLine>
      </div>
    </section>

    {/* CONTENT */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 md:py-28">
      <div className="grid gap-14 lg:grid-cols-12">
        {/* LEFT: DETAILS */}
        <div className="lg:col-span-5 space-y-6">
          {PEOPLE.map((p) => (
            <Reveal key={p.name}>
              <div className="rounded-2xl border border-brand-line bg-white p-7" data-testid={`contact-card-${p.name.split(" ")[0].toLowerCase()}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-muted">{p.role}</p>
                <h3 className="mt-2 font-display text-2xl">{p.name}</h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a href={`tel:${p.tel}`} data-testid={`call-${p.name.split(" ")[0].toLowerCase()}`} className="inline-flex items-center gap-2 rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-brand-cream transition-colors hover:bg-brand-choc">
                    <Phone className="h-4 w-4" /> {p.phone}
                  </a>
                  <a href={p.wa} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-${p.name.split(" ")[0].toLowerCase()}`} className="inline-flex items-center gap-2 rounded-full border border-brand-line px-5 py-2.5 text-sm font-bold text-brand-choc transition-colors hover:border-brand-gold">
                    <MessageCircle className="h-4 w-4 text-[#1FA855]" /> WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="space-y-4 rounded-2xl border border-brand-line bg-brand-sand/50 p-7 text-sm">
              <p className="flex gap-3 text-brand-ink"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />{SITE.address}</p>
              <p className="flex gap-3 items-center"><Mail className="h-4 w-4 shrink-0 text-brand-gold" /><a href={`mailto:${SITE.email}`} data-testid="contact-email" className="link-underline">{SITE.email}</a></p>
              <p className="flex gap-3 items-center"><Globe className="h-4 w-4 shrink-0 text-brand-gold" />{SITE.web}</p>
              <a
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="mt-2 flex items-center justify-center gap-3 rounded-full bg-[#1FA855] px-6 py-4 text-sm font-bold text-white transition-all duration-300 hover:brightness-110"
              >
                <MessageCircle className="h-5 w-5" /> Chat with us on WhatsApp
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>

        {/* RIGHT: FORM */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight">Send an enquiry</h2>
            <p className="mt-3 text-sm text-brand-muted">Tell us what you need — we'll reply with grammages, packing formats and FOB/CIF details.</p>
            <div className="mt-8">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </div>

      {/* MAP */}
      <Reveal delay={0.1}>
        <div className="mt-20 overflow-hidden rounded-2xl border border-brand-line" data-testid="contact-map">
          <iframe
            title="National Biscuit & Sweet Manufacturers on Google Maps"
            src={SITE.mapsEmbed}
            className="h-[380px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        <a
          href={SITE.mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="maps-directions-link"
          className="link-underline mt-4 inline-flex items-center gap-2 text-sm font-bold text-brand-red"
        >
          <MapPin className="h-4 w-4" /> Get directions on Google Maps
        </a>
      </Reveal>
    </section>

    {/* GROUP FIRMS */}
    <section className="border-t border-brand-line bg-brand-sand/50">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 md:py-24">
        <Reveal><p className="eyebrow">The family behind the flour</p></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-display font-light text-3xl md:text-5xl tracking-tight">Our group firms</h2>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="group-firms">
          {GROUP_FIRMS.map((g, i) => (
            <Reveal key={g.name} delay={i * 0.08}>
              <a
                href={g.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`group-firm-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex h-full items-center justify-between gap-4 rounded-2xl border border-brand-line bg-white p-7 transition-all duration-300 hover:border-brand-red hover:shadow-lg"
              >
                <div>
                  <h3 className="font-display text-xl">{g.name}</h3>
                  <p className="mt-1 text-sm text-brand-muted">{g.desc}</p>
                </div>
                <ExternalLink className="h-5 w-5 text-brand-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </main>
);

export default ContactPage;
